import React, { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

// components
import {
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  CircularProgress,
  TablePagination,
} from '@mui/material'
import StatusBadge from '../statusBadge'

// hooks
import useSort from '../../../../hooks/useSort'

// styles
import styles from './bulkUsersUploadStatuses.module.scss'
import { fetchUser } from '../../../../services/user'

function BulkUsersUploadStatuses({
  isFetching,
  bulkUserUploadStatuses,
  searchText,
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(10)
    const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }
  const jobOwner = bulkUserUploadStatuses?.[0]?.jobOwner
  useEffect(() => {
    setEmail('')
    if (jobOwner) {
      const fetchEmail = async () => {
        try {
          const userData = await fetchUser(jobOwner)
          setEmail(userData?.data?.userName)
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error)
        }
      }

      if (searchText?.length > 0) {
        fetchEmail()
      }
    }
  }, [jobOwner, searchText])

  const { sortBy, sortOrder, handleSort } = useSort({
    initialSortBy: '',
    initialSortOrder: 'asc',
  })

  const showOwnerColumn = searchText?.length > 0
  const loaderColSpan = showOwnerColumn ? 4 : 3

  const renderTableHead = () => (
    <TableRow>
      <TableCell className={styles.tableHeader}>
        <TableSortLabel
          active={sortBy === 'processedTimeStamp'}
          direction={sortOrder}
          onClick={() => handleSort('processedTimeStamp')}
        >
          {t('bulk-search-date')}
        </TableSortLabel>
      </TableCell>

      {showOwnerColumn && (
        <TableCell className={styles.tableHeader}>
          <TableSortLabel
            active={sortBy === 'email'}
            direction={sortOrder}
            onClick={() => handleSort('email')}
          >
            {t('bulk-search-file-owner')}
          </TableSortLabel>
        </TableCell>
      )}

      <TableCell className={styles.tableHeader}>
        <TableSortLabel
          active={sortBy === 'fileName'}
          direction={sortOrder}
          onClick={() => handleSort('fileName')}
        >
          {t('bulk-search-job-id')}
        </TableSortLabel>
      </TableCell>

      <TableCell className={styles.tableHeader}>
        <TableSortLabel
          active={sortBy === 'status'}
          direction={sortOrder}
          onClick={() => handleSort('status')}
        >
          {t('bulk-search-status')}
        </TableSortLabel>
      </TableCell>
    </TableRow>
  )

  const renderTableBody = () => (
    bulkUserUploadStatuses
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    .map((bulkUploadStatus) => (
      <TableRow
        className={styles.tableBodyRow}
        key={bulkUploadStatus.id}
        onClick={() => navigate(`/view-bulk-job/${bulkUploadStatus?.id}`)}
      >
        <TableCell
          className={styles.tableCell}
          data-label={t('bulk-search-date')}
        >
          <span className={styles.cellValue}>
            {dayjs(bulkUploadStatus?.creationTime).format(
              'MM/DD/YYYY hh:mm:ssA',
            )}
          </span>
        </TableCell>

        {showOwnerColumn && (
          <TableCell
            className={styles.tableCell}
            data-label={t('bulk-search-file-owner')}
          >
            <span className={styles.cellValue}>{email}</span>
          </TableCell>
        )}

        <TableCell
          className={styles.tableCell}
          data-label={t('bulk-search-job-id')}
        >
          <span className={styles.cellValue}>{bulkUploadStatus?.id}</span>
        </TableCell>

        <TableCell
          className={styles.tableCell}
          data-label={t('bulk-search-status')}
        >
          <span className={styles.cellValue}>
            <StatusBadge
              status={bulkUploadStatus?.currentStatus}
              alternativeStyle
            />
          </span>
        </TableCell>
      </TableRow>
    )))

  const renderLoaderInTableBody = () => (
    <TableRow>
      <TableCell sx={{ border: 'none' }} colSpan={loaderColSpan}>
        <div className={styles.loaderContainer}>
          <div className={styles.loading}>{t('loading')}</div>
          <CircularProgress sx={{ color: 'black' }} />
        </div>
      </TableCell>
    </TableRow>
  )

  const renderTable = () =>
    ((isFetching || bulkUserUploadStatuses?.length > 0) && (
      <div className={styles.tableWrapper}>
        <p className={styles.results}>
          {searchText.length === 0
            ? t('most-recent-results')
            : t('search-results')}
        </p>
        <TableContainer className={styles.tableContainer}>
          <Table stickyHeader className={styles.userTable}>
            <TableHead>{renderTableHead()}</TableHead>
            <TableBody>
              {isFetching ? renderLoaderInTableBody() : renderTableBody()}
            </TableBody>
          </Table>
        </TableContainer>
        {bulkUserUploadStatuses.length > 10 && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={bulkUserUploadStatuses.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t('rows-per-page')}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} ${t('rows-per-page-of')} ${count}`}
          />
        )}
      </div>
    )) ||
    (!isFetching &&
      bulkUserUploadStatuses?.length === 0 &&
      searchText.length === 0 && (
        <div>
          <p>{t('no-bulk-jobs-history')}</p>
        </div>
      ))

  return <div className={styles.container}>{renderTable()}</div>
}

BulkUsersUploadStatuses.propTypes = {
  isFetching: PropTypes.bool.isRequired,
  bulkUserUploadStatuses: PropTypes.array,
  searchText: PropTypes.string,
}

BulkUsersUploadStatuses.defaultProps = {
  bulkUserUploadStatuses: [],
  searchText: '',
}

export default BulkUsersUploadStatuses
